import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ScheduleIcon from '@mui/icons-material//Schedule';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ApiService } from 'api';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import InformationRequestExpirationDatePicker from 'dashboard409a/components/InformationRequestExpirationDatePicker/InformationRequestExpirationDatePicker';
import {
  getDefaultInformationRequestExpirationDate,
  isValidInformationRequestExpirationDate,
  toInformationRequestExpirationIso,
} from 'dashboard409a/utilities';
import { manageApiRequest } from 'hooks/apiRequest';
import {
  TASK_EXPIRATION_CANCEL,
  TASK_EXPIRATION_DATE_PICKER_ID,
  TASK_EXPIRATION_DATE_PICKER_LABEL,
  TASK_EXPIRATION_DAYS_LABEL,
  TASK_EXPIRATION_EMPTY_COUNTDOWN,
  TASK_EXPIRATION_EXPIRED,
  TASK_EXPIRATION_EXPIRED_CONTACT_SCALAR,
  TASK_EXPIRATION_EXPIRES_IN,
  TASK_EXPIRATION_EXTEND_BUTTON,
  TASK_EXPIRATION_EXTEND_BUTTON_ID,
  TASK_EXPIRATION_EXTEND_CONFIRM_ID,
  TASK_EXPIRATION_EXTEND_DIALOG_TITLE,
  TASK_EXPIRATION_EXTEND_ERROR,
  TASK_EXPIRATION_EXTEND_SUCCESS,
  TASK_EXPIRATION_HOURS_LABEL,
  TASK_EXPIRATION_MINUTES_LABEL,
  TASK_EXPIRATION_SAVE,
  TASK_EXPIRATION_SECONDS_LABEL,
} from 'pages/ProcessManagement/components/ProcessManagementHeader/ProcessManagementHeader.constants';
import {
  formatExpirationDate,
  getExpirationDate,
  getInformationRequestStatusId,
  isExpirationDatePassed,
} from 'pages/ProcessManagement/utils/expirationDate';
import { useUserUtils } from 'utilities/userUtils';
import TimeBlock from './TimeBlock';

const TaskExpirationDate = () => {
  const { task, requestedFiles, requestedQuestions, validateToken } = useContext(TaskContext) as TaskContextType;
  const { isStaffUser, isSuperUser } = useUserUtils();
  const canExtendRequest = isStaffUser() || isSuperUser();
  const expirationDate = useMemo(() => getExpirationDate(task.expiration_date), [task.expiration_date]);
  const [formattedExpirationDate, setFormattedExpirationDate] = useState(() =>
    expirationDate ? formatExpirationDate(expirationDate) : TASK_EXPIRATION_EMPTY_COUNTDOWN
  );
  const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false);
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const informationRequestStatusId = useMemo(
    () => getInformationRequestStatusId(requestedFiles, requestedQuestions, task.requested_files),
    [requestedFiles, requestedQuestions, task.requested_files]
  );

  const updateFormattedDate = useCallback(() => {
    if (!expirationDate) {
      setFormattedExpirationDate(TASK_EXPIRATION_EMPTY_COUNTDOWN);
      return;
    }
    setFormattedExpirationDate(formatExpirationDate(expirationDate));
  }, [expirationDate]);

  useEffect(() => {
    updateFormattedDate();
    const interval = setInterval(updateFormattedDate, 1000);
    return () => clearInterval(interval);
  }, [updateFormattedDate]);

  const isExpired = !expirationDate || isExpirationDatePassed(expirationDate);
  const [days, hours, minutes, seconds] = formattedExpirationDate.split(' ');

  const handleOpenExtendDialog = () => {
    setSelectedExpirationDate(
      expirationDate && !isExpirationDatePassed(expirationDate)
        ? expirationDate
        : getDefaultInformationRequestExpirationDate()
    );
    setIsExtendDialogOpen(true);
  };

  const handleCloseExtendDialog = () => {
    if (isSaving) {
      return;
    }
    setIsExtendDialogOpen(false);
  };

  const handleConfirmExtend = async () => {
    if (!informationRequestStatusId || !isValidInformationRequestExpirationDate(selectedExpirationDate)) {
      return;
    }

    setIsSaving(true);
    const response = await manageApiRequest(
      ApiService.apiUpdateInformationRequestStatusUpdate(String(informationRequestStatusId), {
        expiration_date: toInformationRequestExpirationIso(selectedExpirationDate),
        send_email: false,
      }),
      {
        successMessage: TASK_EXPIRATION_EXTEND_SUCCESS,
        errorMessage: TASK_EXPIRATION_EXTEND_ERROR,
      }
    );

    setIsSaving(false);
    if (!response) {
      return;
    }

    setIsExtendDialogOpen(false);
    validateToken();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {isExpired ? (
        <Box display="flex" alignItems="center" marginBottom="0.5rem">
          <ScheduleIcon style={{ marginRight: '0.5rem', color: '#8A9097', fontSize: '1rem' }} />
          <Typography style={{ fontSize: '1rem', fontWeight: 400, textTransform: 'uppercase', color: '#8A9097' }}>
            {canExtendRequest ? TASK_EXPIRATION_EXPIRED : TASK_EXPIRATION_EXPIRED_CONTACT_SCALAR}
          </Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center" marginBottom="0.5rem">
            <ScheduleIcon style={{ marginRight: '0.5rem', color: '#8A9097', fontSize: '1rem' }} />
            <Typography style={{ fontSize: '1rem', fontWeight: 400, textTransform: 'uppercase', color: '#8A9097' }}>
              {TASK_EXPIRATION_EXPIRES_IN}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            <TimeBlock value={days} label={TASK_EXPIRATION_DAYS_LABEL} />
            <TimeBlock value={hours} label={TASK_EXPIRATION_HOURS_LABEL} />
            <TimeBlock value={minutes} label={TASK_EXPIRATION_MINUTES_LABEL} />
            <TimeBlock value={seconds} label={TASK_EXPIRATION_SECONDS_LABEL} />
          </Box>
        </>
      )}
      {canExtendRequest && (
        <Button
          id={TASK_EXPIRATION_EXTEND_BUTTON_ID}
          data-testid={TASK_EXPIRATION_EXTEND_BUTTON_ID}
          size="small"
          variant="outlined"
          onClick={handleOpenExtendDialog}
          disabled={!informationRequestStatusId}
          sx={{ mt: 0.5, textTransform: 'none' }}>
          {TASK_EXPIRATION_EXTEND_BUTTON}
        </Button>
      )}
      <Dialog open={isExtendDialogOpen} onClose={handleCloseExtendDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{TASK_EXPIRATION_EXTEND_DIALOG_TITLE}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <InformationRequestExpirationDatePicker
              id={TASK_EXPIRATION_DATE_PICKER_ID}
              label={TASK_EXPIRATION_DATE_PICKER_LABEL}
              value={selectedExpirationDate}
              onChange={setSelectedExpirationDate}
              disabled={isSaving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtendDialog} disabled={isSaving}>
            {TASK_EXPIRATION_CANCEL}
          </Button>
          <Button
            id={TASK_EXPIRATION_EXTEND_CONFIRM_ID}
            data-testid={TASK_EXPIRATION_EXTEND_CONFIRM_ID}
            variant="contained"
            onClick={handleConfirmExtend}
            disabled={isSaving || !isValidInformationRequestExpirationDate(selectedExpirationDate)}>
            {TASK_EXPIRATION_SAVE}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskExpirationDate;
